import MomentUtils from '@date-io/moment';
import React, { useState, useEffect } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { ThemeProvider } from '@material-ui/styles';
import { createBrowserHistory } from 'history';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { Provider as StoreProvider } from 'react-redux';
import { Route, Router, Switch } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import './assets/css/arcelikFont.css';
import './assets/css/google.css';
import './assets/scss/main.scss';
import GoogleAnalytics from './components/GoogleAnalytics';

import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { LoginCallback, Security } from '@okta/okta-react';
import { oktaClientId, oktaIssuer } from 'src/utils/contants';
import { SDKProvider } from './components/ScanditSDKContext';
import ScrollReset from './components/ScrollReset';
import StylesProvider from './components/StylesProvider';
import Auth from './layouts/Auth';
import './modules/mixins/chartjs';
import './modules/mixins/moment';
import './modules/mixins/prismjs';
import './modules/mixins/validate';
import { theme } from './modules/theme';
import { configureStore } from './store';




const { store, persistor } = configureStore();
const history = createBrowserHistory();

// Development ortamında HTTPS kullanılmıyorsa cookie ayarlarını değiştir
const isProduction = process.env.NODE_ENV === 'production';
const isHttps = window.location.protocol === 'https:';

const config = {
  issuer: oktaIssuer,
  redirectUri: `${window.location.origin}/implicit/callback`,
  clientId: oktaClientId,
  pkce: true,
  scopes: ['openid', 'email', 'profile', 'offline_access'], // refresh_token için offline_access eklendi
  cookies: {
    secure: isHttps, // HTTPS kullanılıyorsa true, değilse false
    sameSite: isHttps ? 'None' : 'Lax', // HTTPS yoksa Lax kullan
  },
  tokenManager: {
    storage: 'localStorage', // Alternatif: sessionStorage veya özel secureStorage
    autoRenew: true,
    autoRemove: true,
    secure: true, // Token'ların güvenli saklanması için
    syncStorage: true,
    expireEarlySeconds: 120, // Token süresinin dolmasına 30 saniye kala yenile
    useInteractionCode: false,
    clearTokenBeforeRefresh: true,
  },
  disableHttpsCheck: !isProduction, // Development'ta HTTPS kontrolünü devre dışı bırak
};

const oktaAuth = new OktaAuth(config);
const restoreOriginalUri = async (_oktaAuth, originalUri) => {
  history.replace(toRelativeUrl(originalUri, window.location.origin));
};

// İzin bildirimi için global event sistemi
const permissionEvents = {
  listeners: [],
  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  },
  emit(data) {
    this.listeners.forEach(callback => callback(data));
  }
};

// Aynı uyarının tekrar tekrar gösterilmesini engellemek için
const shownWarnings = new Set();
const WARNING_COOLDOWN = 30000; // 30 saniye aynı uyarıyı gösterme

// Global erişim için window'a ekle
window.showPermissionWarning = (type) => {
  // Eğer bu tür uyarı son 30 saniye içinde gösterildiyse, tekrar gösterme
  if (shownWarnings.has(type)) {
    return;
  }
  
  shownWarnings.add(type);
  
  // 30 saniye sonra tekrar gösterilebilir hale getir
  setTimeout(() => {
    shownWarnings.delete(type);
  }, WARNING_COOLDOWN);

  const messages = {
    camera: '📷 Kamera izni reddedildi. Barkod okuma ve fotoğraf çekme özelliklerini kullanabilmek için tarayıcı ayarlarından kamera iznini aktif edin.',
    microphone: '🎤 Mikrofon izni reddedildi. Ses analizi özelliğini kullanabilmek için tarayıcı ayarlarından mikrofon iznini aktif edin.',
    location: '📍 Konum izni reddedildi. Konum tabanlı özellikleri kullanabilmek için tarayıcı ayarlarından konum iznini aktif edin.',
    general: '⚠️ Cihaz izni reddedildi. İlgili özellikleri kullanabilmek için tarayıcı ayarlarından gerekli izinleri aktif edin.'
  };
  permissionEvents.emit({ message: messages[type] || messages.general, type });
};

window.addEventListener('unhandledrejection', function (event) {
  const message = event.reason?.message || "";
  const errorName = event.reason?.name || "";
  
  // İzin türünü belirle ve kullanıcıya bildir
  const isPermissionError = 
    errorName === "NotAllowedError" ||
    errorName === "PermissionDeniedError" ||
    message.includes("Permission denied") ||
    message.includes("Permission dismissed") ||
    message.includes("user denied permission") ||
    message.includes("not allowed by the user agent") ||
    message.includes("denied permission");

  // Kamera, mikrofon, konum ve diğer izin hatalarını handle et
  if (
    message.includes("audio device") || 
    message.includes("camera") || 
    message.includes("Camera") ||
    message.includes("TrackStartError") ||
    message.includes("NotReadableError") ||
    message.includes("Could not start video source") ||
    message.includes("Could not initialize camera") ||
    message.includes("initialize camera") ||
    message.includes("Track is in an invalid state") ||
    message.includes("invalid state") ||
    message.includes("Permission denied") ||
    message.includes("Permission dismissed") ||
    message.includes("user denied permission") ||
    message.includes("not allowed by the user agent") ||
    message.includes("denied permission") ||
    errorName === "NotReadableError" ||
    errorName === "NotFoundError" ||
    errorName === "NotAllowedError" ||
    errorName === "OverconstrainedError" ||
    errorName === "OperationError" ||
    errorName === "PermissionDeniedError" ||
    errorName === "GeolocationPositionError"
  ) {
    // İzin hatası ise kullanıcıya bildir
    if (isPermissionError) {
      // Hangi tür izin olduğunu belirlemeye çalış
      if (message.includes("camera") || message.includes("Camera") || message.includes("video")) {
        window.showPermissionWarning('camera');
      } else if (message.includes("audio") || message.includes("microphone")) {
        window.showPermissionWarning('microphone');
      } else if (message.includes("geolocation") || message.includes("location") || errorName === "GeolocationPositionError") {
        window.showPermissionWarning('location');
      } else {
        window.showPermissionWarning('general');
      }
    }
    event.preventDefault();
  }
});


function App() {
  const [permissionAlert, setPermissionAlert] = useState({ open: false, message: '', type: '' });

  useEffect(() => {
    // İzin uyarılarını dinle
    const unsubscribe = permissionEvents.subscribe(({ message, type }) => {
      setPermissionAlert({ open: true, message, type });
    });
    return () => unsubscribe();
  }, []);

  const handleCloseAlert = (event, reason) => {
    if (reason === 'clickaway') return;
    setPermissionAlert(prev => ({ ...prev, open: false }));
  };

  return (
    <StoreProvider store={store}>
      <PersistGate loading={<CircularProgress status={'loading'} />} persistor={persistor}>
        <ThemeProvider theme={theme}>
          <StylesProvider direction="ltr">
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <SDKProvider>
                <Router history={history}>
                  <ScrollReset />
                  <GoogleAnalytics />
                  {/* İzin Uyarısı Snackbar */}
                  <Snackbar 
                    open={permissionAlert.open} 
                    autoHideDuration={8000} 
                    onClose={handleCloseAlert}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                  >
                    <MuiAlert 
                      elevation={6} 
                      variant="filled" 
                      onClose={handleCloseAlert} 
                      severity="warning"
                      style={{ fontSize: '14px', maxWidth: '90vw' }}
                    >
                      {permissionAlert.message}
                    </MuiAlert>
                  </Snackbar>
                  <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
                    <Switch>
                      <Route
                        exact
                        path="/implicit/callback"
                        component={LoginCallback}
                      />
                      <Route path="/" component={Auth} />
                    </Switch>
                  </Security>
                </Router>
              </SDKProvider>
            </MuiPickersUtilsProvider>
          </StylesProvider>
        </ThemeProvider>
      </PersistGate>
    </StoreProvider>
  );
}

export default App;
